import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonIcon,
  IonSkeletonText,
} from '@ionic/react';
import ProCard from './ProCard';
import {
  documentOutline,
  chevronDownOutline,
  chevronUpOutline,
  alertCircleOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import ProMobileBillCard from './ProMobileBillCard';
import { Bill } from '../../types/bill';

/**
 * Pro-namespaced Hot Bills dashboard widget.
 *
 * Fork of the shared `components/mobile/BillsCard` that adds an `onBillClick`
 * prop so the Pro home page can route taps to `/pro/bills/:billNumber`
 * instead of the default web `/legislation/*` route. Uses
 * {@link ProMobileBillCard} for light-mode-aware rendering.
 */

interface BillSummary {
  id: string;
  billNumber: string;
  title: string;
  status: string;
  priority: 'high' | 'medium' | 'low';
  isHot: boolean;
  lastUpdated: Date;
  lastAction: string;
  workspaceName: string;
  workspaceNames?: string[];
  workspaceIds?: string[];
  calendarEvents?: any[];
  nextCalendarEvent?: any;
}

interface BillsStatusData {
  hotBills: BillSummary[];
  trackedBills: BillSummary[];
  updatedSinceLastLogin: number;
  totalTracked: number;
}

export interface ProBillsCardProps {
  queryKey?: string;
  refetchRef?: React.MutableRefObject<(() => Promise<any>) | null>;
  maxItems?: number;
  onBillClick?: (bill: Bill) => void;
}

const mapBillSummaryToBill = (bill: BillSummary): Bill => ({
  id: bill.id,
  billNumber: bill.billNumber,
  title: bill.title,
  status: bill.status,
  isHot: bill.isHot,
  lastActivity: bill.lastUpdated?.toString() || '',
  workspaceName: bill.workspaceName,
  workspaceNames: bill.workspaceNames || [bill.workspaceName].filter(Boolean),
  workspaceIds: bill.workspaceIds || [],
  calendarEvents: bill.calendarEvents || [],
  nextCalendarEvent: bill.nextCalendarEvent || null,
  billPosition: undefined,
  concerning: '',
  session: '',
  subject: [],
  summary: '',
  location: '',
  introducedDate: null,
  sponsors: [],
  committees: [],
  categories: [],
  notes: '',
});

const ProBillsCard: React.FC<ProBillsCardProps> = ({
  queryKey = 'pro-bills-status',
  refetchRef,
  maxItems = 5,
  onBillClick,
}) => {
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<BillsStatusData>(
    [queryKey],
    async () => {
      const response = await api.get('/dashboard/widgets/bills_status/data');
      return response.data?.data?.data || response.data?.data || response.data;
    },
    { staleTime: 300000, refetchInterval: 600000 },
  );

  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetchRef, refetch]);

  const hotBills: Bill[] = React.useMemo(() => {
    if (!data?.hotBills) return [];
    return data.hotBills.map(mapBillSummaryToBill);
  }, [data]);

  const displayBills = showAll ? hotBills : hotBills.slice(0, maxItems);
  const hasMore = hotBills.length > maxItems;

  return (
    <ProCard padded>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 rounded-xl bg-background-quaternary dark:bg-background-dark-tertiary space-y-2">
                <IonSkeletonText animated style={{ width: '30%', height: '14px' }} />
                <IonSkeletonText animated style={{ width: '80%', height: '16px' }} />
                <IonSkeletonText animated style={{ width: '50%', height: '12px' }} />
                <IonSkeletonText animated style={{ width: '40%', height: '12px' }} />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <IonIcon icon={alertCircleOutline} className="text-3xl text-error-500 mb-2" />
            <p className="text-sm text-error-600 dark:text-error-400 mb-2">
              Unable to load bills data.
            </p>
            <IonButton fill="clear" size="small" onClick={() => refetch()}>
              Try again
            </IonButton>
          </div>
        ) : hotBills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <IonIcon icon={documentOutline} className="text-3xl text-text-tertiary dark:text-text-dark-tertiary mb-2 opacity-50" />
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              No hot bills requiring attention
            </p>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
              Bills marked as &quot;hot&quot; will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayBills.map((bill) => (
              <ProMobileBillCard
                key={bill.id}
                bill={bill}
                isHotBill={true}
                flush
                workspaceName={bill.workspaceName}
                onClick={onBillClick}
              />
            ))}

            {hasMore && (
              <IonButton
                fill="clear"
                size="small"
                onClick={() => setShowAll(!showAll)}
                expand="block"
              >
                <IonIcon icon={showAll ? chevronUpOutline : chevronDownOutline} slot="start" />
                {showAll ? 'Show less' : `Show all ${hotBills.length} hot bills`}
              </IonButton>
            )}
          </div>
        )}
    </ProCard>
  );
};

export default ProBillsCard;
